// Shared chrome for the District Insights / Impact dashboards: the boxes,
// tables, controls and headings every tab is built out of.
//
// Why this file exists: each tab grew its own version of the same six or seven
// elements, so the pages disagreed on card borders, row dividers, column-head
// treatment, button radii and — most visibly — on what a segmented control
// looks like. Everything here is deliberately presentational; the data models
// stay in tokens.js and the tabs.
//
// The canonical decisions, so a new tab doesn't have to re-litigate them:
//
//   • A segmented control is a TRAY with a raised SURFACE pill (SegControl).
//     The solid-INK fill is reserved for *navigation* (the concern nav in
//     dashboard/chrome.js), so "which page am I on" never looks like "which
//     sort order is active".
//   • Control labels are micro-caps above the control (FieldLabel), not inline
//     sentence case beside it. `inline` exists for control BARS — the page header
//     strip and the per-page ControlBar — where controls sit on one line.
//   • Controls sort into three placements, and the placement carries meaning:
//     SCOPE (what data is on screen) goes in the page's ControlBar; SHAPE (what
//     form the graphic takes) goes in that graphic's own SectionHead; DISPLAY
//     (sort, thresholds, encoding) collapses into a DisplayMenu beside them. A
//     reader should be able to tell what a control will disturb from where it
//     sits.
//   • Choosing between sub-views of a page is NAVIGATION, so it's SubTabs in the
//     ControlBar, not a SegControl labelled "Lens" — the pills are for options.
//   • Tables live in a TableCard and get micro-cap column heads. Nesting is
//     shown by an indented, recessed panel (nestBg) — never by restyling the
//     table itself.
//   • Numbers are tabular-nums whenever they sit in a column.
import React, { useState, useEffect, useRef } from "react";
import {
  INK,
  MUTED,
  GRID,
  SURFACE,
  CARD_RADIUS,
  CARD_BORDER,
  ROW_DIVIDER,
  TRAY,
  PROSE,
} from "./tokens";

// ---- measurement -----------------------------------------------------------

// Tracked width of a DOM node. For a layout that has to switch on the space it
// actually got rather than on the viewport: between window.innerWidth and the
// width a panel here receives sit the admin sidebar, the frosted page panel and
// two levels of card padding, so a media query breaks at a number that has to be
// re-derived every time any of that chrome changes. Measuring the element itself
// can't drift.
//
// Returns [ref, width]. Width is null until the first observation, and callers
// should treat null as "render the narrow layout" — narrow is the arrangement
// that is correct at every size, so the first paint is never wrong, only
// conservative.
const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // jsdom (tests) and pre-13 Safari have no ResizeObserver. One measurement is
    // better than none; the layout then simply stays where it was through a
    // resize.
    if (typeof ResizeObserver === "undefined") {
      setWidth(el.getBoundingClientRect().width);
      return;
    }
    // Two guards, both needed.
    //
    // Deferred to the next frame, because a layout that switches on this width
    // can change the page's height, which can add or remove the document
    // scrollbar, which changes this width — all inside the observer's own
    // delivery. The browser reports that as "ResizeObserver loop completed with
    // undelivered notifications" and CRA's dev overlay puts it on screen as an
    // uncaught error. Setting state a frame later puts the observation and the
    // layout it causes in different frames, so there is no loop to complete.
    //
    // And guarded against re-setting the same number, so a scrollbar that
    // toggles back and forth settles instead of re-rendering forever.
    let frame = null;
    const ro = new ResizeObserver((entries) => {
      const w = entries[entries.length - 1].contentRect.width;
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        setWidth((prev) => (prev === w ? prev : w));
      });
    });
    ro.observe(el);
    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);
  return [ref, width];
};

// ---- surfaces --------------------------------------------------------------

// A Card's default padding, exported because plots that must line up with a
// neighbouring card's inner content have to subtract it from their own gutters
// (see the ribbon/mix pair on Adoption → Usage).
const CARD_PAD = 16;

// The white box everything sits on, over the frosted page panel.
const Card = ({ pad = CARD_PAD, children, style }) => (
  <div
    style={{
      background: SURFACE,
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: CARD_RADIUS,
      padding: pad,
      ...style,
    }}
  >
    {children}
  </div>
);

// A Card sized for a chart canvas. Chart.js needs a fixed pixel height on the
// container (maintainAspectRatio: false everywhere in these tabs).
const ChartCard = ({ height = 320, children, style }) => (
  <Card style={{ height, ...style }}>{children}</Card>
);

// A Card holding a table: no padding of its own (rows run edge to edge) and
// overflow hidden so the corner radius clips the header band.
const TableCard = ({ children, style }) => (
  <Card pad={0} style={{ overflow: "hidden", ...style }}>
    {children}
  </Card>
);

// ---- tables ----------------------------------------------------------------

// How many rows a worklist shows before it is expanded. A display default, so it
// sits with the table rather than with the model whose rows it truncates.
const DEFAULT_ROWS = 6;


// Two sizes: `md` for a top-level table, `sm` for one nested inside an expanded
// row. Only type scale and padding change — column heads, dividers and
// alignment stay identical, which is what makes a nested table read as the same
// object one level down.
const TABLE_SIZES = {
  md: { body: 13, head: 11, padY: 8, headPadY: 8 },
  sm: { body: 12.5, head: 10.5, padY: 6, headPadY: 4 },
};

const TableSizeContext = React.createContext("md");

// `minWidth` keeps a wide table from crushing its columns — it scrolls inside
// the wrapper instead. Pick it to match the sum of the column widths.
//
// `fixed` (tableLayout: fixed) makes the Th widths authoritative instead of
// advisory. Use it whenever a table has a free-text column: auto layout sizes
// columns to their content, so a single long unbroken value — an org that named
// a school "Thisisarealllylongschoolname…" — expands that column and shoves the
// measure columns off the right edge. With `fixed`, the labelled columns keep
// their widths and the text column absorbs the remainder (pair it with
// `truncate` on those cells).
const Table = ({ size = "md", minWidth, fixed, children, style }) => (
  <TableSizeContext.Provider value={size}>
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          minWidth,
          borderCollapse: "collapse",
          tableLayout: fixed ? "fixed" : undefined,
          fontSize: TABLE_SIZES[size].body,
          ...style,
        }}
      >
        {children}
      </table>
    </div>
  </TableSizeContext.Provider>
);

// Micro-cap column-head band. Sits on a faint tint so the header reads as
// chrome rather than as the first row of data.
const HeadRow = ({ children, tint = true }) => {
  const size = React.useContext(TableSizeContext);
  return (
    <thead>
      <tr
        style={{
          textAlign: "left",
          color: MUTED,
          fontSize: TABLE_SIZES[size].head,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          fontWeight: 600,
          background: tint ? TRAY : "transparent",
        }}
      >
        {children}
      </tr>
    </thead>
  );
};

// `hint` is lower-case running text appended to a head label (e.g. the bar
// mode on the curriculum table) — it opts out of the micro-caps treatment so
// the parenthetical stays readable.
const Th = ({ align = "left", width, children, hint, style, ...rest }) => {
  const size = React.useContext(TableSizeContext);
  return (
    <th
      style={{
        padding: `${TABLE_SIZES[size].headPadY}px 8px`,
        textAlign: align,
        width,
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {children}
      {hint && (
        <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
          {" "}
          {hint}
        </span>
      )}
    </th>
  );
};

// Sortable column header. Clicking sorts by this column; clicking the active
// column reverses it.
//
// The direction mark is ALWAYS drawn, faint on the inactive columns rather than
// revealed on hover — the same rule the concern nav's chevron follows. A cue that
// only appears once the reader has already guessed the header is clickable isn't
// doing the job.
//
// `defaultDir` is which way this column sorts on first click, and it belongs with
// the column rather than in the sort state: names want A→Z, measures want
// biggest-first, and a single global default makes one of the two wrong.
const SortTh = ({
  sortKey,
  sort,
  onSort,
  defaultDir = "desc",
  align = "left",
  width,
  children,
  title,
}) => {
  const active = sort && sort.key === sortKey;
  const dir = active ? sort.dir : null;
  return (
    <Th
      align={align}
      width={width}
      style={{ padding: 0 }}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() =>
          onSort(
            active
              ? { key: sortKey, dir: dir === "asc" ? "desc" : "asc" }
              : { key: sortKey, dir: defaultDir }
          )
        }
        // a clicked header kept the browser's focus ring, which read as a second
        // "active column" state; keyboard focus still shows its own
        onMouseDown={(e) => e.preventDefault()}
        title={title || `Sort by ${typeof children === "string" ? children.toLowerCase() : "this column"}`}
        style={{
          // inherits the head row's micro-caps treatment, so a sortable header
          // and a plain one are the same object with one extra glyph
          font: "inherit",
          letterSpacing: "inherit",
          textTransform: "inherit",
          color: active ? INK : "inherit",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "7px 8px",
          width: "100%",
          textAlign: align,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: align === "right" ? "flex-end" : "flex-start",
          gap: 4,
        }}
      >
        {children}
        <span
          aria-hidden="true"
          style={{
            fontSize: 8,
            lineHeight: 1,
            opacity: active ? 0.9 : 0.3,
            letterSpacing: 0,
          }}
        >
          {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "▼"}
        </span>
      </button>
    </Th>
  );
};

// Right/center-aligned cells get tabular figures so columns of numbers line up.
// `truncate` ellipsises overlong free text on one line — pass a `title` with the
// full value alongside it.
const Td = ({ align = "left", truncate, children, style, ...rest }) => {
  const size = React.useContext(TableSizeContext);
  return (
    <td
      style={{
        padding: `${TABLE_SIZES[size].padY}px 8px`,
        textAlign: align,
        verticalAlign: "top",
        fontVariantNumeric: align === "right" ? "tabular-nums" : undefined,
        ...(truncate
          ? {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 0, // required for ellipsis to engage in a table cell
            }
          : null),
        ...style,
      }}
      {...rest}
    >
      {children}
    </td>
  );
};

// A body row. `flush` drops the divider — use it for the row that carries an
// expanded panel, so the panel joins the row above it.
const Row = ({ children, flush, style, ...rest }) => (
  <tr
    style={{
      borderTop: flush ? "none" : `1px solid ${ROW_DIVIDER}`,
      ...style,
    }}
    {...rest}
  >
    {children}
  </tr>
);

// Empty / loading / error state inside a TableCard, on its own divider so it
// lines up with where rows would be.
const TableNote = ({ children, tone }) => (
  <div
    style={{
      padding: "18px 16px",
      color: tone || MUTED,
      fontSize: 12.5,
      borderTop: `1px solid ${ROW_DIVIDER}`,
    }}
  >
    {children}
  </div>
);

// ---- controls --------------------------------------------------------------

// Micro-caps control label. Stacked above its control by default.
const FieldLabel = ({ children, inline }) => (
  <span
    style={{
      fontSize: inline ? 12 : 11,
      letterSpacing: inline ? 0 : ".06em",
      textTransform: inline ? "none" : "uppercase",
      color: MUTED,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

// Wraps a control with its label — stacked (default) or on one line (`inline`,
// for the page header strip).
const Field = ({ label, inline, children, style }) => (
  <div
    style={{
      display: inline ? "inline-flex" : "flex",
      flexDirection: inline ? "row" : "column",
      alignItems: inline ? "center" : "flex-start",
      gap: inline ? 8 : 6,
      ...style,
    }}
  >
    {label && <FieldLabel inline={inline}>{label}</FieldLabel>}
    {children}
  </div>
);

// THE segmented control. `options` are [key, label] or
// [key, label, { disabled, title }].
const SegControl = ({ label, value, onChange, options, inline, style }) => (
  <Field label={label} inline={inline} style={style}>
    <div
      style={{
        display: "inline-flex",
        background: TRAY,
        border: `1px solid ${GRID}`,
        borderRadius: 9,
        padding: 2,
        gap: 2,
      }}
    >
      {options.map(([key, text, opt]) => {
        const on = key === value;
        const disabled = !!(opt && opt.disabled);
        return (
          <button
            key={String(key)}
            type="button"
            onClick={() => !disabled && onChange(key)}
            disabled={disabled}
            title={opt && opt.title}
            aria-pressed={on}
            style={{
              border: "none",
              background: on ? SURFACE : "transparent",
              color: disabled ? "#bcbbb4" : on ? INK : MUTED,
              boxShadow: on ? "0 1px 2px rgba(0,0,0,.10)" : "none",
              fontWeight: on ? 600 : 500,
              borderRadius: 7,
              padding: "5px 10px",
              fontSize: 13,
              cursor: disabled ? "not-allowed" : "pointer",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </button>
        );
      })}
    </div>
  </Field>
);

// Sub-navigation within a page: which lens of this concern am I looking at.
//
// Deliberately NOT a SegControl. A lens swaps the entire page body, and rendered
// as a labelled pill tray it looked exactly like "Sort" or "Bar" — options that
// nudge one graphic. Underlined tabs read as navigation while staying clearly
// subordinate to the concern nav above them, which owns the solid-INK fill.
//
// Sits on the ControlBar's hairline: the active tab's 2px rule overlaps it (-1px
// margin), so the selected tab looks joined to the content below.
const SubTabs = ({ value, onChange, options, style }) => (
  <div style={{ display: "inline-flex", gap: 20, ...style }}>
    {options.map(([key, text, opt]) => {
      const on = key === value;
      return (
        <button
          key={String(key)}
          type="button"
          onClick={() => onChange(key)}
          // A clicked tab drew the browser's focus box around itself, which read
          // as a third state on top of the active underline. Suppressing focus on
          // mousedown only affects pointer clicks — keyboard focus still lands and
          // still shows its ring.
          onMouseDown={(e) => e.preventDefault()}
          title={opt && opt.title}
          aria-current={on ? "page" : undefined}
          style={{
            border: "none",
            background: "transparent",
            padding: "2px 1px 9px",
            marginBottom: -1,
            fontSize: 14.5,
            fontWeight: on ? 700 : 500,
            color: on ? INK : MUTED,
            borderBottom: `2px solid ${on ? INK : "transparent"}`,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </button>
      );
    })}
  </div>
);

// The page's control bar: sub-tabs on the left, the scope controls that govern
// this lens on the right, one hairline under the lot.
//
// It exists to stop the chrome from growing downward. As a stacked micro-cap
// label over a pill tray with its own 16px margin, two controls cost ~130px
// before any data — and the curriculum lens, with eleven, costs more than a plot.
// Inline labels put the whole set on one ~36px line, and the hairline gives the
// page a single place where "what am I looking at" is answered.
const ControlBar = ({ tabs, children, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      flexWrap: "wrap",
      // Row gap is generous because a wrapped bar puts the controls on their own
      // line under the tabs, where they need to clear the active tab's rule.
      gap: "10px 24px",
      borderBottom: `1px solid ${GRID}`,
      marginBottom: 16,
      ...style,
    }}
  >
    {/* a placeholder keeps the controls right-aligned on a bar with no tabs */}
    {tabs || <span />}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px 18px",
        // clears the hairline the same way the tabs' rule does
        paddingBottom: 8,
      }}
    >
      {children}
    </div>
  </div>
);

// The third placement: DISPLAY options — sort order, thresholds, encodings. They
// change how the rows in hand are presented, never which rows were fetched, and
// they are set once and then left alone, so they don't earn permanent width next
// to the controls a reader touches on every question.
//
// Collapsed, but never *hidden*: a non-default display setting silently filtering
// or reordering a table is the failure mode this has to avoid, so the trigger
// carries a count of what's been changed from default and the summary line under
// the graphic still spells the settings out.
//
// `changed` is the number of non-default settings; `summary` (optional) is shown
// inside the open panel. Children are ordinary stacked SegControls.
const DisplayMenu = ({ label = "Display", changed = 0, children, style }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={wrap} style={{ position: "relative", ...style }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: `1px solid ${GRID}`,
          background: open ? TRAY : SURFACE,
          color: INK,
          borderRadius: 9,
          padding: "6px 10px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {/* Two stacked sliders, drawn rather than imported: this is the only
            icon in the file and it has to sit on the control's own baseline. */}
        <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
          <g
            stroke={MUTED}
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
          >
            <path d="M2 5h12M2 11h12" />
          </g>
          <circle cx="6" cy="5" r="2.1" fill={SURFACE} stroke={INK} strokeWidth="1.5" />
          <circle cx="10.5" cy="11" r="2.1" fill={SURFACE} stroke={INK} strokeWidth="1.5" />
        </svg>
        {label}
        {changed > 0 && (
          <span
            title={`${changed} setting${changed === 1 ? "" : "s"} changed from default`}
            style={{
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: INK,
              color: SURFACE,
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
              padding: "0 4px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {changed}
          </span>
        )}
      </button>
      {open && (
        <div
          role="group"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 30,
            background: SURFACE,
            border: CARD_BORDER,
            borderRadius: CARD_RADIUS,
            boxShadow: "0 8px 24px rgba(0,0,0,.13)",
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 14,
            // wide enough for the longest pill row inside ("All levels ·
            // Grade K only"), and it may hang left of the trigger.
            minWidth: 210,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// The dropdown counterpart to SegControl, for choices too numerous to be pills
// (schools, school years). `options` are [value, label] or
// [value, label, { disabled }].
const Select = ({
  label,
  value,
  onChange,
  options,
  inline,
  title,
  maxWidth,
  style,
}) => (
  <Field label={label} inline={inline} style={style} >
    <select
      value={value == null ? "" : String(value)}
      onChange={(e) => onChange(e.target.value)}
      title={title}
      style={{
        border: `1px solid ${GRID}`,
        borderRadius: 9,
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 600,
        color: INK,
        background: SURFACE,
        cursor: "pointer",
        maxWidth,
      }}
    >
      {options.map(([v, text, opt]) => (
        <option key={String(v)} value={String(v)} disabled={opt && opt.disabled}>
          {text}
        </option>
      ))}
    </select>
  </Field>
);

// Secondary action: "Show all 17 schools", "Show 11 units with thin data".
const GhostButton = ({ onClick, children, style }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: `1px solid ${GRID}`,
      background: SURFACE,
      color: INK,
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      ...style,
    }}
  >
    {children}
  </button>
);

// Row-expansion affordance. Keeps its slot when disabled so columns stay
// aligned down the table.
const Caret = ({ open, onClick, title, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    aria-expanded={!!open}
    title={title}
    style={{
      border: "none",
      background: "transparent",
      cursor: disabled ? "default" : "pointer",
      color: disabled ? GRID : INK,
      fontSize: 12,
      lineHeight: 1,
      padding: 4,
    }}
  >
    {open ? "▾" : "▸"}
  </button>
);

// ---- headings + prose ------------------------------------------------------

// Section heading with an optional right-aligned muted note (counts, caveats,
// a link to the full list).
// `controls` holds this section's SHAPE controls — the ones that redraw the
// graphic directly beneath and nothing else (Subject on the dose→response). They
// belong on the heading line rather than in a row of their own: it's a line of
// vertical space saved, and the adjacency is what says "this only affects that".
// `sub` is a caption UNDER the title, for the sentence that says what the thing
// below is — what a list is sorted by, how many of it is showing. `note` is the
// right-aligned variant, which works when it's the only thing sharing the line;
// with `controls` present too, a space-between row puts the note in the middle of
// the heading and it reads as neither. Same idea as PanelHead's `sub`, one level
// up.
const SectionHead = ({ title, note, sub, controls, level = 3, style }) => {
  const H = `h${level}`;
  return (
    <div
      style={{
        display: "flex",
        // controls have height, so baseline alignment would hang them off the
        // heading's baseline instead of sitting them on it
        alignItems: controls ? "flex-end" : "baseline",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        margin: "0 0 8px",
        ...style,
      }}
    >
      {/* The heading side is the flexible one: it SHRINKS, and its sub-line wraps
          inside it, so the controls keep their place on the row.

          Without `minWidth: 0` a flex item refuses to shrink below the intrinsic
          width of its text, so a heading whose sub-line grows pushes the controls
          onto a line of their own — which is what the Adoption worklist did the
          moment its sub-line picked up a sort phrase ("sorted by standards per
          learner, highest first (back to priority order)"). The controls moving
          because you sorted a column is a layout jump with no meaning behind it.

          `flex: 0 0 auto` on the controls is the other half: a control is a fixed
          object with a hit target, and shrinking a <select> below its content
          clips the option text. */}
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <H style={{ margin: 0 }}>{title}</H>
        {sub && (
          <div style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>{sub}</div>
        )}
      </div>
      {note && (
        <span style={{ color: MUTED, fontSize: 12, flex: "0 1 auto" }}>
          {note}
        </span>
      )}
      {controls && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "6px 16px",
            flex: "0 0 auto",
          }}
        >
          {controls}
        </div>
      )}
    </div>
  );
};

// Caption for one panel inside a section that holds two of them — the ribbon and
// the mix under "How the year was used". Subordinate to SectionHead by weight
// rather than by indentation, and it carries the SHAPE controls for its own
// panel on the right (`controls`), which is where a control that redraws exactly
// one graphic belongs.
const PanelHead = ({ title, sub, controls, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "6px 16px",
      margin: "0 0 8px",
      ...style,
    }}
  >
    <div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{title}</div>
      {sub && (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{sub}</div>
      )}
    </div>
    {controls && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "6px 16px",
        }}
      >
        {controls}
      </div>
    )}
  </div>
);

// Explanatory body copy under a heading.
const Prose = ({ children, style }) => (
  <p style={{ color: PROSE, margin: "0 0 16px", maxWidth: "72ch", ...style }}>
    {children}
  </p>
);

// Smaller, quieter copy: the methodology note under a table or chart.
const Footnote = ({ children, style }) => (
  <p
    style={{
      color: MUTED,
      fontSize: 12,
      lineHeight: 1.5,
      margin: "12px 0 0",
      maxWidth: "76ch",
      ...style,
    }}
  >
    {children}
  </p>
);

// ---- legend + tiles --------------------------------------------------------

// `pattern` is a CSS background-image, for a state the grid distinguishes by
// texture rather than by fill; `outline` draws a hairline so a near-white fill
// still reads as a swatch and not as a gap in the key.
const Swatch = ({ color, shape = "square", pattern, outline }) => {
  if (shape === "line")
    return (
      <span
        style={{
          width: 14,
          height: 3,
          borderRadius: 2,
          background: color,
          display: "inline-block",
        }}
      />
    );
  if (shape === "dash")
    return (
      <span
        style={{
          width: 14,
          height: 0,
          borderTop: `3px dashed ${color}`,
          display: "inline-block",
        }}
      />
    );
  return (
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: 3,
        // Longhand beside `backgroundImage`, or React warns on rerender.
        backgroundColor: color,
        backgroundImage: pattern || "none",
        border: outline ? `1px solid ${outline}` : "none",
        display: "inline-block",
        flex: "none",
      }}
    />
  );
};

// Chart/bar key. `items` are
// { color, label, note?, shape?, muted?, pattern?, outline?, title? }; `trailing`
// is pushed to the far right (a count or scope caption).
const Legend = ({ items, trailing, style }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 16,
      alignItems: "center",
      margin: "0 0 8px",
      ...style,
    }}
  >
    {items.map((it) => (
      <span
        key={it.label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 12.5,
          color: it.muted ? MUTED : INK,
        }}
      >
        <Swatch
          color={it.color}
          shape={it.shape}
          pattern={it.pattern}
          outline={it.outline}
        />
        {/* Series labels are user-supplied names — cap them so one very long
            school doesn't push the rest of the key onto its own line. */}
        <span
          title={it.title || it.label}
          style={{
            maxWidth: "34ch",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {it.label}
        </span>
        {it.note && <span style={{ color: MUTED }}>· {it.note}</span>}
      </span>
    ))}
    {trailing && (
      <span style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>
        {trailing}
      </span>
    )}
  </div>
);

// ---- stat rail ---------------------------------------------------------------
// One surface holding several label/value/sub cells, instead of one bordered card
// per number.
//
// One bordered card per figure makes four boxes inside a box and spends a lot of
// vertical space doing it: a summary strip is one reading of one thing, not
// several separate objects. Both the Overview's summary band and the curriculum
// strip use this, because the same strip in two places should not be two
// different components.
// `hint` explains the number rather than restating it -- specifically, a
// denominator the reader cannot derive from what is on screen. It hangs off the
// VALUE, not the cell, because the value is what it qualifies, and it carries
// the dotted-underline + help-cursor affordance FocusTab's legend uses: a
// `title` with no affordance is a hover nobody discovers they can perform, which
// is the same as not writing it.
const RailStat = ({ label, value, sub, hint, color, children }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        fontSize: 11,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        color: MUTED,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    {value !== undefined && (
      <div
        style={{
          fontSize: 22,
          fontWeight: 650,
          color: color || INK,
          marginTop: 2,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {/* An inline-block span, not the block above: the underline has to be
            the width of the number, and on the block it would run the width of
            the grid cell and read as a rule under the stat. */}
        {hint && value != null ? (
          <span
            title={hint}
            style={{
              cursor: "help",
              borderBottom: `1px dotted ${GRID}`,
            }}
          >
            {value}
          </span>
        ) : value == null ? (
          "—"
        ) : (
          value
        )}
      </div>
    )}
    {sub && (
      <div style={{ fontSize: 12, color: MUTED, marginTop: 2, lineHeight: 1.35 }}>
        {sub}
      </div>
    )}
    {children}
  </div>
);

// The row the cells sit in. `divided` adds the hairline rule + spacing used when
// the rail shares a card with something above it (the Overview's hero sentence);
// standing alone it needs no rule, just a Card around it.
const StatRail = ({ min = 170, divided, children, style }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: "14px 28px",
      ...(divided
        ? { marginTop: 16, paddingTop: 14, borderTop: `1px solid ${GRID}` }
        : {}),
      ...style,
    }}
  >
    {children}
  </div>
);

// ---- plot captions -----------------------------------------------------------
// The caption that belongs UNDER a plot, not above it.
//
// Every tab opened with a paragraph explaining the chart that followed, which
// meant the reader scrolled past 4–6 lines of methodology to reach the picture
// they came for — and on a laptop the plot itself often started below the fold.
// The reading order that actually works is: see the plot, then read one line
// saying what it is, and only then (if you want it) the caveats.
//
// So: `children` is the one-line caption — what the marks are, in a sentence.
// `details` is everything else (denominators, sampling caveats, what colors
// mean), folded behind a disclosure that costs one line until it's opened.
// Inline disclosure rather than a modal: it keeps the explanation next to the
// thing it explains, survives print/screenshot, and needs no focus trap.
const PlotNote = ({ children, details, label = "How to read this", style }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ margin: "10px 0 24px", maxWidth: "84ch", ...style }}>
      <div style={{ color: MUTED, fontSize: 12.5, lineHeight: 1.55 }}>
        {children}
        {details && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                fontWeight: 600,
                color: "#2a78d6",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {label} {open ? "▴" : "▾"}
            </button>
          </>
        )}
      </div>
      {open && details && (
        <div
          style={{
            marginTop: 8,
            padding: "11px 14px",
            background: TRAY,
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: 8,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: PROSE,
          }}
        >
          {details}
        </div>
      )}
    </div>
  );
};

export {
  CARD_PAD,
  DEFAULT_ROWS,
  useElementWidth,
  Card,
  ChartCard,
  TableCard,
  Table,
  HeadRow,
  Th,
  SortTh,
  Td,
  Row,
  TableNote,
  FieldLabel,
  Field,
  SegControl,
  SubTabs,
  ControlBar,
  DisplayMenu,
  Select,
  GhostButton,
  Caret,
  SectionHead,
  PanelHead,
  Prose,
  Footnote,
  PlotNote,
  Swatch,
  Legend,
  RailStat,
  StatRail,
};
